/* eslint-disable max-len */
import { useState } from "react";
import { ImageField, ImageInput, useTranslate } from "react-admin";
import { muiColors } from "themes/colors";
import { TrashIcon } from "assets/Icons";
import { Box, IconButton } from "@mui/material";
import ConfirmationModal from "./ConfirmationModal";

interface CustomImageInputProps {
  source: string;
  label?: string;
  width?: string;
  height?: string;
  badgeNumber?: string | number;
}

const CustomImageInput = ({
  source,
  label = " ",
  width = "100%",
  height = "180px",
  badgeNumber
}: CustomImageInputProps) => {
  const [error, setError] = useState("");
  const translate = useTranslate();
  return (
    <Box
      sx={{
        ":not(.previews .RaImageField-image)": {
          ".RaFileInput-removeButton button": {
            display: "none"
          }
        },
        ":has(.previews .RaImageField-image)": {
          ".CustomRemoveIcon": {
            opacity: 1
          },
          ".RaFileInput-removeButton button": {
            display: "flex"
          }
        },
        ".fetchedImageByEdit img": {
          position: "absolute",
          top: "22px",
          left: "0px",
          zIndex: 1,
          objectFit: "cover !important",
          width: "100% !important",
          height: `${height} !important`,
          margin: "0px !important",
          borderRadius: "8px"
        }
      }}
      style={{
        width: width,
        minWidth: "250px",
        position: "relative"
      }}
    >
      <ImageInput
        label={label}
        source={source}
        accept="image/*"
        placeholder=".PNG or .JPG"
        maxSize={10000000}
        options={{
          onDropRejected(fileRejections) {
            if (fileRejections?.[0]?.errors?.[0]?.code === "file-too-large") {
              setError(
                "The file you attempted to upload exceeds our 10MB limit. Please choose a file that is smaller in size."
              );
            } else {
              setError("Invalid file. Please choose other file.");
            }
          }
        }}
        sx={{
          "& .RaFileInput-dropZone": {
            height: height,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            border: "solid 1px #F2EFEA",
            borderRadius: "8px",
            fontSize: "12px",
            color: muiColors.text.disabled
          },
          "& .RaFileInput-removeButton": {
            position: "absolute",
            top: "22px",
            left: "0px",
            width: "100%",
            zIndex: 2,
            button: {
              opacity: 1,
              top: "unset",
              right: "unset",
              bottom: "-47px",
              left: "95.5px",
              zIndex: 1,
              svg: {
                opacity: 0
              }
            }
          },
          "& .RaImageField-image": {
            objectFit: "cover !important",
            width: "100% !important",
            height: `${height} !important`,
            margin: "0px !important",
            borderRadius: "8px"
          },
          "& .MuiFormHelperText-root": {
            display: "none"
          }
        }}
      >
        <ImageField source="src" />
      </ImageInput>
      <ImageField source={source} className="fetchedImageByEdit" />
      {badgeNumber && (
        <div
          className="absolute bottom-[70px] left-[8px] text-[12px] text-mono-primaryTextColor
        font-[500] bg-mono-primaryLightBgColor rounded-[64px] leading-[20px] px-[7px] z-10"
        >
          <span>{badgeNumber}</span>
          {badgeNumber === 1 && <span> - Cover</span>}
        </div>
      )}
      <div className="mt-[6px] mb-[16px] flex items-center gap-[8px]">
        <Box
          component="label"
          htmlFor={source}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "30px",
            fontSize: "14px",
            fontFamily: "Inter",
            lineHeight: "16px",
            fontWeight: 500,
            borderRadius: "32px",
            textTransform: "none",
            whiteSpace: "nowrap",
            backgroundColor: muiColors.button.paper,
            color: muiColors.text.primary,
            border: `solid 1px ${muiColors.button.border}`,
            padding: "0px 10px",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: muiColors.button.secondaryHover
            }
          }}
        >
          {translate("common.set_image")}
        </Box>
        <IconButton
          className="CustomRemoveIcon"
          sx={{ opacity: 0, pointerEvents: "none" }}
        >
          <TrashIcon />
        </IconButton>
      </div>
      {error && (
        <ConfirmationModal
          open={Boolean(error)}
          handleConfirm={() => setError("")}
          onClose={() => setError("")}
          text={error}
          buttonText="Got it"
          title="Warning"
          buttonBg={muiColors.button.primary}
          centerAlign
        />
      )}
    </Box>
  );
};

export default CustomImageInput;
